import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Heart } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardFooter } from './ui/card';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useWishlist } from '../contexts/WishlistContext';
import { toast } from 'sonner';

export const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const [isAnimating, setIsAnimating] = useState(false);

  const isFavorited = isInWishlist(product.id);

  const handleAddToCart = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Faça login para adicionar ao carrinho');
      navigate('/auth');
      return;
    }
    try {
      await addToCart(product.id, 1);
      toast.success('Produto adicionado ao carrinho!');
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
    }
  };

  const handleToggleWishlist = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Faça login para adicionar aos favoritos');
      navigate('/auth');
      return;
    }

    const result = await toggleWishlist(product.id);
    
    if (result.success) {
      if (result.action === 'added') {
        // Trigger animation
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 650);
        
        toast.success('Adicionado aos favoritos!', {
          icon: '💖',
          duration: 1500,
        });
      } else {
        toast.success('Removido dos favoritos');
      }
    } else {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  return (
    <Card
      className="group cursor-pointer hover:shadow-2xl hover:shadow-cyan-500/20 hover:-translate-y-2 transition-all duration-500 overflow-hidden border border-slate-200 dark:border-slate-700 hover:border-cyan-500/50 bg-white dark:bg-gradient-to-br dark:from-slate-800 dark:to-[#0f172a] rounded-2xl shadow-lg"
      onClick={() => navigate(`/product/${product.id}`)}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-[#0f172a] dark:to-slate-800 aspect-square">
        <img
          src={product.images[0] || 'https://via.placeholder.com/400'}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/80 via-blue-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-sm">
            <span className="bg-red-500 text-white px-6 py-2 rounded-full font-semibold shadow-lg">Esgotado</span>
          </div>
        )}
        
        {/* Featured badge */}
        {product.featured && (
          <div className="absolute top-3 left-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg shadow-cyan-500/30 flex items-center gap-1">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></span>
            Destaque
          </div>
        )}
        
        {/* Wishlist button */}
        <button
          onClick={handleToggleWishlist}
          className={`absolute top-3 right-3 p-2.5 backdrop-blur-sm rounded-full shadow-lg border transition-all duration-300 hover:scale-110 ${
            isFavorited 
              ? 'bg-pink-500 border-pink-400 opacity-100 shadow-pink-500/40' 
              : 'bg-white/90 dark:bg-[#0f172a]/90 border-transparent hover:border-pink-500/50 opacity-0 group-hover:opacity-100 hover:bg-pink-50 dark:hover:bg-pink-900/30'
          } ${isAnimating ? 'animate-ping-once' : ''}`}
          data-testid={`wishlist-btn-${product.id}`}
        >
          <Heart 
            className={`h-5 w-5 transition-all duration-300 ${
              isFavorited 
                ? 'text-white fill-white' 
                : 'text-slate-600 dark:text-slate-300 hover:text-pink-500'
            } ${isAnimating ? 'scale-125' : ''}`} 
          />
          {/* Floating hearts animation */}
          {isAnimating && !isFavorited && (
            <>
              <span className="absolute -top-1 -left-1 text-pink-500 animate-float-up-1">💖</span>
              <span className="absolute -top-1 -right-1 text-pink-500 animate-float-up-2">💕</span>
              <span className="absolute top-0 left-1/2 -translate-x-1/2 text-pink-500 animate-float-up-3">❤️</span>
            </>
          )}
        </button>
        
        {/* Quick add button on hover */}
        <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-4 group-hover:translate-y-0">
          <Button
            onClick={handleAddToCart}
            disabled={product.stock <= 0}
            className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white shadow-xl shadow-cyan-500/30 rounded-xl py-5 font-semibold"
            data-testid={`add-to-cart-btn-${product.id}`}
          >
            <ShoppingCart className="h-4 w-4 mr-2" />
            Adicionar ao Carrinho
          </Button>
        </div>
      </div>
      
      <CardContent className="p-5 bg-gradient-to-b from-transparent to-slate-50/50 dark:to-[#0f172a]/50">
        <h3 className="font-bold text-lg text-slate-900 dark:text-white mb-2 line-clamp-1 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
          {product.name}
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-sm line-clamp-2 mb-4">{product.description}</p>
        <div className="flex items-end justify-between">
          <div>
            <span className="text-xs text-slate-400 dark:text-slate-500 block">A partir de</span>
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-500 to-blue-600 bg-clip-text text-transparent">
              R$ {product.price.toFixed(2).replace('.', ',')}
            </span>
          </div>
          {product.stock > 0 && (
            <span className="text-xs text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 px-2.5 py-1 rounded-full font-medium border border-cyan-200 dark:border-cyan-800">
              Em estoque
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};